import { useState } from "react";
import { Helmet } from "react-helmet-async";
import HFHeader from "@/components/HFHeader";
import HFFooter from "@/components/HFFooter";
import Video360Viewer from "@/components/Video360Viewer";
import ImmersiveGallery, { type ImmersiveItem } from "@/components/ImmersiveGallery";
import SmartMedia from "@/components/SmartMedia";
import HeroCarousel, { heroItemsWithFallback, type HeroItem } from "@/components/HeroCarousel";
import ViewerErrorBoundary from "@/components/ViewerErrorBoundary";
import ContactForm from "@/components/ContactForm";
import HomeGallery from "@/components/HomeGallery";
import AktionHeroChip from "@/components/AktionHeroChip";
import AktionSection from "@/components/AktionSection";
import BeforeAfterSection from "@/components/BeforeAfterSection";
import Transformations360Section from "@/components/Transformations360Section";
import DiscretionSection from "@/components/DiscretionSection";
import TrustBoxSection from "@/components/TrustBoxSection";
import UseCaseTile from "@/components/UseCaseTile";
import RichText from "@/components/RichText";
import { canonical, absoluteAsset } from "@/lib/siteConfig";
import { Container, HFLinkButton, SectionHeader, HFButton } from "@/components/ui-hf";
import { useSiteContent, isEnabled } from "@/lib/siteContent";
import { resolveMediaUrl, type MediaRef } from "@/lib/media";
import { HERO_BG_MAP, HERO_BG_DEFAULT, HeroBgKey } from "@/lib/homeContent";
import {
    Camera,
    Film,
    Sparkles,
    Heart,
    Hammer,
    Ruler,
    Key,
    Trees,
    Sun,
    BookOpen,
    Leaf,
    ArrowLeftRight,
    HardHat,
    Archive,
    ChevronDown,
    Phone,
    Mail,
    MapPin,
} from "lucide-react";
import {
    HERO,
    IDEA,
    IMMERSIVE,
    LENSES,
    COMPARE,
    USE_CASES,
    PROCESS,
    PACKAGES,
    ABOUT,
    FAQ,
    BDV_CONTACT,
} from "@/lib/homeContent";

const USE_ICON_MAP: Record<string, React.ReactNode> = {
    heart: <Heart size={20} />,
    hammer: <Hammer size={20} />,
    ruler: <Ruler size={20} />,
    key: <Key size={20} />,
    tree: <Trees size={20} />,
    sun: <Sun size={20} />,
    book: <BookOpen size={20} />,
    leaf: <Leaf size={20} />,
    arrows: <ArrowLeftRight size={20} />,
    hardhat: <HardHat size={20} />,
    archive: <Archive size={20} />,
};

// Historisches USE_CASE_EXAMPLES-Mapping wurde entfernt — Kacheln kommen jetzt
// vollständig aus dem CMS (`home.use_cases.tiles[]`). Falls eine Kachel ein
// `skybox`-Feld setzt, wird der 360°-Button gezeigt; sonst nur „Foto ansehen".

function scrollTo(id: string) {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function HomePage() {
    const [contactPkg, setContactPkg] = useState("");
    const [openFaq, setOpenFaq] = useState<number | null>(0);
    const { get, getObject, sectionEnabled } = useSiteContent();

    // Globale Section-Sichtbarkeit (Draft-Feld `enabled: false` in site_content).
    // Default = sichtbar; nur explizites `false` blendet die Sektion global aus.
    const showHero = sectionEnabled("home.hero");
    const showIdea = sectionEnabled("home.idea");
    const showImmersive = sectionEnabled("home.immersive");
    const showLenses = sectionEnabled("home.lenses");
    const showCompare = sectionEnabled("home.compare");
    const showUseCases = sectionEnabled("home.use_cases");
    const showGallery = sectionEnabled("home.gallery");
    const showBeforeAfter = sectionEnabled("home.before_after");
    const showTr360 = sectionEnabled("home.transformations360");
    const showProcess = sectionEnabled("home.process");
    const showPackages = sectionEnabled("home.packages");
    const showAbout = sectionEnabled("home.about");
    const showFaq = sectionEnabled("home.faq");
    const showContact = sectionEnabled("home.contact");

    // Phase-2A CMS: strukturierte Section-JSONs. Falls (noch) nicht gesetzt,
    // greifen die statischen Fallback-Konstanten aus homeContent.ts.
    type SectionMedia = { path?: string; media_id?: string } | undefined;
    interface HomeHeroSection { background?: SectionMedia; items?: HeroItem[] }
    interface HomeIdeaSection { image?: SectionMedia }
    interface HomeImmersiveSection { video?: SectionMedia; poster?: SectionMedia; items?: unknown[] }
    interface HomeAboutSection { image?: SectionMedia }
    interface HomeUseCasesSection { tiles?: { image?: SectionMedia }[] }
    interface HomeGallerySection { tiles?: unknown[] }

    const homeHero = getObject<HomeHeroSection>("home.hero", {});
    const homeIdea = getObject<HomeIdeaSection>("home.idea", {});
    const homeImmersive = getObject<HomeImmersiveSection>("home.immersive", {});
    const homeAbout = getObject<HomeAboutSection>("home.about", {});
    const homeUseCases = getObject<HomeUseCasesSection>("home.use_cases", {});
    void getObject<HomeGallerySection>("home.gallery", {}); // reserviert für Phase 2b

    // CMS-Overlays: neuer Editor speichert Section-JSON (home.hero.*); ältere
    // flat-Keys bleiben als Fallback erlaubt für rückwärtskompatible Overrides.
    const pick = (obj: Record<string, unknown> | undefined, k: string, flatKey: string, dflt: string) => {
        const v = obj?.[k];
        if (typeof v === "string" && v.trim()) return v;
        return get<string>(flatKey, dflt);
    };
    const homeIdeaObj = homeIdea as unknown as Record<string, unknown>;
    const homeHeroObj = homeHero as unknown as Record<string, unknown>;
    const homeImmersiveObj = homeImmersive as unknown as Record<string, unknown>;
    const homeAboutObj = homeAbout as unknown as Record<string, unknown>;
    const heroEyebrow = pick(homeHeroObj, "eyebrow", "home.hero.eyebrow", HERO.eyebrow);
    const heroHeadline = pick(homeHeroObj, "headline", "home.hero.headline", HERO.headline);
    const heroSubline = pick(homeHeroObj, "subline", "home.hero.subline", HERO.subline);
    const ideaEyebrow = pick(homeIdeaObj, "eyebrow", "home.idea.eyebrow", IDEA.eyebrow);
    const ideaHeadline = pick(homeIdeaObj, "headline", "home.idea.headline", IDEA.headline);
    const ideaBody = pick(homeIdeaObj, "body", "home.idea.body", "").trim();
    const viewerHeadline = pick(homeImmersiveObj, "headline", "home.viewer.headline", IMMERSIVE.headline);
    const viewerInstructions = pick(homeImmersiveObj, "lead", "home.viewer.instructions", IMMERSIVE.lead);
    const lensesHeadline = get<string>("home.lenses.headline", LENSES.headline);
    const lensesBody = get<string>("home.lenses.body", "").trim();
    const aboutHeadline = pick(homeAboutObj, "headline", "home.about.headline", ABOUT.headline);
    const aboutBody = pick(homeAboutObj, "body", "home.about.body", "").trim();
    const homeContactObj = getObject<Record<string, unknown>>("home.contact", {});
    const contactPhone = pick(homeContactObj, "phone", "contact.phone", BDV_CONTACT.phone);
    const contactEmail = pick(homeContactObj, "email", "contact.email", BDV_CONTACT.email);
    const contactAddress = pick(
        homeContactObj, "address", "contact.address",
        `${BDV_CONTACT.street}\n${BDV_CONTACT.zip} ${BDV_CONTACT.city}`,
    );

    const ideaParas = ideaBody ? ideaBody.split(/\n\s*\n/) : IDEA.paragraphs;
    const lensesParas = lensesBody ? lensesBody.split(/\n\s*\n/) : LENSES.paragraphs;
    const aboutParas = aboutBody ? aboutBody.split(/\n\s*\n/) : ABOUT.paragraphs;

    // Hero-Hintergrund per CMS-Variante: neuer Editor schreibt `background_variant`,
    // legacy schreibt flat-Key `home.hero.background`. Beides akzeptieren.
    const bgKey =
        (typeof homeHeroObj.background_variant === "string" ? (homeHeroObj.background_variant as string) : "") ||
        get<string>("home.hero.background", HERO_BG_DEFAULT);
    const heroBg = HERO_BG_MAP[bgKey as HeroBgKey] || HERO_BG_MAP[HERO_BG_DEFAULT];
    // Konkrete Media überschreibt die Variant-Wahl.
    const heroBgUrl = resolveMediaUrl(homeHero.background as MediaRef, heroBg.src);

    // Hero-Items: neues Multi-Item-Schema mit Fallback auf Legacy `background`.
    const heroItems = heroItemsWithFallback(homeHero.items, homeHero.background as MediaRef);

    const canonicalUrl = canonical("/");
    const heroImageAbs = absoluteAsset(heroBgUrl);
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: "HypeFly",
        image: heroImageAbs,
        url: canonicalUrl,
        telephone: "+49 211 61 66 33 00",
        email: BDV_CONTACT.email,
        description:
            "HypeFly dokumentiert Immobilien als vollständiges 360°-Video-Zeitdokument — ergänzt um hochwertige 360°-Fotos. Digitales Archiv für Familien, Verkauf und Nachlass. Marke der BDV GmbH, Düsseldorf.",
        serviceType: "360°-Video-Immobiliendokumentation",
        areaServed: {"@type": "Country", name: "Deutschland"},
        address: {
            "@type": "PostalAddress",
            streetAddress: BDV_CONTACT.street,
            postalCode: BDV_CONTACT.zip,
            addressLocality: BDV_CONTACT.city,
            addressCountry: "DE",
        },
        parentOrganization: {
            "@type": "Organization",
            name: BDV_CONTACT.company,
            url: "https://www.b-d-v.de/",
        },
    };

    const pickPackage = (name: string) => {
        setContactPkg(name);
        scrollTo("kontakt");
    };

    return (
        <div className="min-h-screen">
            <Helmet>
                <title>HypeFly – 360°-Video-Immobiliendokumentation für bleibende Erinnerungen</title>
                <meta
                    name="description"
                    content="HypeFly dokumentiert Immobilien als vollständiges 360°-Video-Zeitdokument — ergänzt um hochwertige 360°-Fotos. Digitales Archiv für Familien, Verkauf, Umbau oder Nachlass. Persönlich, diskret, dauerhaft. Marke der BDV GmbH."
                />
                <link rel="canonical" href={canonicalUrl} />
                <link rel="preload" as="image" href={heroBgUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="HypeFly – 360°-Video-Immobiliendokumentation" />
                <meta
                    property="og:description"
                    content="Ein digitales Gedächtnis Ihrer Immobilie. Vollständig als 360°-Video dokumentiert, ergänzt um hochwertige 360°-Fotos."
                />
                <meta property="og:image" content={heroImageAbs} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary_large_image" />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>
            <HFHeader />

            {/* HERO
              *  Zwei Varianten:
              *   - villa-original → Legacy-Hero (heller Cream-Hintergrund, dunkler Text) [pre-imagery]
              *   - alle anderen   → Editorial-Bild-Hero (Sylt-artig, dunkles Overlay, weißer Text)
              *  Umschaltung via CMS-Key `home.hero.background` in /admin/startseite.
              */}
            <main id="main-content">
            {showHero && (bgKey === "villa-original" ? (
                /* old hero — pre-imagery (Legacy). Zurücksetzen im Admin über home.hero.background = "villa-original". */
                <section
                    id="hero"
                    data-testid="section-hero"
                    className="relative overflow-hidden"
                    style={{ minHeight: "min(92vh, 900px)" }}
                >
                    <div className="absolute inset-0 -z-10">
                        <HeroCarousel
                            items={heroItems}
                            fallbackUrl={heroBg.src}
                            fallbackAlt={heroBg.label}
                            testIdPrefix="hero-bg-carousel"
                            className="w-full h-full"
                        >
                            <div
                                className="absolute inset-0"
                                style={{
                                    background: `linear-gradient(180deg, hsl(var(--hf-bg) / ${(heroBg.overlay * 0.4).toFixed(2)}) 0%, hsl(var(--hf-bg) / ${(heroBg.overlay * 0.15).toFixed(2)}) 45%, hsl(var(--hf-bg) / ${(0.55 + heroBg.overlay * 0.4).toFixed(2)}) 100%)`,
                                    zIndex: 10,
                                    pointerEvents: "none",
                                }}
                            />
                        </HeroCarousel>
                    </div>
                    <Container className="relative pt-20 md:pt-32 pb-24 md:pb-40">
                        <div className="max-w-3xl">
                            <div className="hf-eyebrow" style={{ color: "hsl(var(--hf-gold-dark))" }}>
                                {heroEyebrow}
                            </div>
                            <h1
                                className="mt-5 font-display text-4xl sm:text-6xl md:text-7xl leading-[1.02] text-hf-ink whitespace-pre-line"
                                data-testid="hero-headline"
                            >
                                {heroHeadline}
                            </h1>
                            <div className="mt-4"><AktionHeroChip /></div>
                            <p className="mt-6 max-w-2xl text-base sm:text-lg md:text-xl text-hf-ink-2 leading-relaxed">
                                <RichText html={heroSubline} inline testId="hero-subline" />
                            </p>
                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <HFButton onClick={() => scrollTo("immersive")} variant="primary" trailing={<span aria-hidden>→</span>} data-testid="hero-cta-360">
                                    360°-Video-Beispiel erleben
                                </HFButton>
                                <HFButton onClick={() => scrollTo("kontakt")} variant="secondary" trailing={<span aria-hidden>→</span>} data-testid="hero-cta-contact">
                                    Unverbindlich anfragen
                                </HFButton>
                            </div>
                            <div className="mt-10 flex flex-wrap items-center gap-4 text-xs md:text-sm text-hf-ink-2">
                                {HERO.usps.map((u, i) => (
                                    <span key={u} className="flex items-center gap-3">
                                        {i > 0 && (
                                            <span className="inline-block w-1 h-1 rounded-full" style={{ background: "hsl(var(--hf-gold))" }} />
                                        )}
                                        <span className="uppercase tracking-[0.14em] font-medium">{u}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Container>
                </section>
            ) : (
                /* Editorial-Bild-Hero — dieselbe Ästhetik wie /sylt (dunkles Overlay, heller Text). */
                <section
                    id="hero"
                    data-testid="section-hero"
                    className="relative overflow-hidden"
                    style={{ minHeight: "min(70vh, 760px)", background: "hsl(var(--hf-ink))" }}
                >
                    <HeroCarousel
                        items={heroItems}
                        fallbackUrl={heroBg.src}
                        fallbackAlt={heroBg.label}
                        testIdPrefix="hero-bg-carousel"
                        className="absolute inset-0 w-full h-full"
                    />
                    <div
                        className="absolute inset-0"
                        style={{ opacity: 0.68, pointerEvents: "none" }}
                    />
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(180deg, hsl(var(--hf-ink) / 0.20) 0%, hsl(var(--hf-ink) / 0.40) 55%, hsl(var(--hf-ink) / ${(heroBg.overlayEnd ?? 0.9).toFixed(2)}) 100%)`,
                            pointerEvents: "none",
                        }}
                    />
                    <Container className="relative pt-28 md:pt-40 pb-20 md:pb-28 min-h-[70vh] flex flex-col justify-end">
                        <div className="max-w-4xl">
                            <div
                                className="hf-eyebrow inline-flex items-center gap-3"
                                style={{ color: "hsl(var(--hf-gold-soft))" }}
                            >
                                <span className="inline-block w-8 h-px" style={{ background: "hsl(var(--hf-gold-soft))" }} />
                                {heroEyebrow}
                            </div>
                            <h1
                                className="mt-6 font-display text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.02] whitespace-pre-line"
                                style={{ color: "hsl(var(--hf-bg))" }}
                                data-testid="hero-headline"
                            >
                                {heroHeadline}
                            </h1>
                            <div className="mt-4"><AktionHeroChip /></div>
                            <p
                                className="mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed"
                                style={{ color: "hsl(var(--hf-bg) / 0.90)" }}
                            >
                                <RichText html={heroSubline} inline />
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
                                <HFButton onClick={() => scrollTo("immersive")} variant="primary" trailing={<span aria-hidden>→</span>} data-testid="hero-cta-360">
                                    360°-Video-Beispiel erleben
                                </HFButton>
                                <button
                                    type="button"
                                    onClick={() => scrollTo("kontakt")}
                                    className="hf-btn hf-btn-ghost inline-flex items-center gap-2"
                                    style={{ color: "hsl(var(--hf-bg))", borderColor: "hsl(var(--hf-bg) / 0.35)" }}
                                    data-testid="hero-cta-contact"
                                >
                                    Unverbindlich anfragen <span aria-hidden>→</span>
                                </button>
                            </div>
                            <div
                                className="mt-12 flex flex-wrap gap-x-8 gap-y-2 text-[11px] tracking-[0.28em] uppercase"
                                style={{ color: "hsl(var(--hf-gold-soft))" }}
                                data-testid="hero-usps"
                            >
                                {HERO.usps.map((u) => (
                                    <span key={u}>◇ {u}</span>
                                ))}
                            </div>
                        </div>
                    </Container>
                </section>
            ))}

            {/* IDEA */}
            <AktionSection />
            {showIdea && (
            <section id="idea" data-testid="section-idea" className="py-20 md:py-32">
                <Container className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div>
                        <SectionHeader eyebrow={ideaEyebrow} title={ideaHeadline} />
                        <div className="space-y-5 text-base md:text-lg text-hf-muted leading-relaxed">
                            {ideaBody ? (
                                <RichText html={ideaBody} testId="idea-body" />
                            ) : (
                                ideaParas.map((p, i) => <p key={i}>{p}</p>)
                            )}
                        </div>
                    </div>
                    <div className="relative">
                        <SmartMedia
                            mediaRef={homeIdea.image as MediaRef}
                            /* Static-Fallback NUR wenn das Feld nie gesetzt war
                             * (undefined). Sobald der User es explizit gelöscht
                             * hat, ist `image === null` — dann bleibt die Kachel
                             * leer (SmartMedia rendert einen dezenten Skeleton
                             * bzw. bei Video den MediaPlaceholder). Verhindert
                             * dass die alte Stock-Grafik nach dem Delete
                             * wieder auftaucht. */
                            fallbackUrl={homeIdea.image === undefined ? IDEA.image : undefined}
                            alt="Warm belichtete Detailaufnahme eines Anwesens"
                            width={800}
                            height={1000}
                            loading="lazy"
                            className="w-full h-full object-cover rounded-sm"
                            testId="idea-image"
                            interactive360={true}
                            videoAutoplay
                            videoMuted
                            videoLoop
                            videoControls={false}
                        />
                    </div>
                </Container>
            </section>
            )}

            {/* Vertrauens-Box (dunkle Trust-Sektion, direkt nach „Die Idee").
             *  Sichtbarkeit + Inhalte im Admin unter /admin/vertrauen. */}
            <TrustBoxSection />

            {/* IMMERSIVE 360 */}
            {showImmersive && (
            <section
                id="immersive"
                data-testid="section-immersive"
                className="py-20 md:py-32"
                style={{ background: "hsl(var(--hf-surface) / 0.6)" }}
            >
                <Container>
                    <SectionHeader
                        eyebrow={IMMERSIVE.eyebrow}
                        title={viewerHeadline}
                        lead={viewerInstructions}
                    />
                    {(() => {
                        // Neues Multi-Item-Schema (bis zu 4 Kacheln). Fallback:
                        // wenn `items` fehlt aber Legacy-`video`/`poster` gesetzt
                        // ist, synthetisiere items[0] daraus.
                        const rawItems = (homeImmersive.items as ImmersiveItem[] | undefined) || [];
                        let items = rawItems.filter(isEnabled).slice(0, 4);
                        if (items.length === 0 && homeImmersive.video) {
                            items = [{
                                id: "legacy",
                                title: "360°-Video",
                                enabled: true,
                                video: homeImmersive.video as MediaRef,
                                poster: homeImmersive.poster as MediaRef,
                            }];
                        }
                        if (items.length === 0) {
                            // Ultimate fallback: alte Demo, damit Section nicht leer bleibt.
                            items = [{
                                id: "demo",
                                title: "Demo",
                                enabled: true,
                                video: "/media/demo-360-web.mp4" as unknown as MediaRef,
                                poster: "/media/demo-360-poster.jpg" as unknown as MediaRef,
                            }];
                        }
                        return <ImmersiveGallery items={items} />;
                    })()}
                </Container>
            </section>
            )}

            {/* LENSES */}
            {showLenses && (
            <section
                id="lenses"
                data-testid="section-lenses"
                className="py-20 md:py-32 overflow-x-hidden"
            >
                <Container className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] gap-8 xl:gap-12 items-center px-6 md:px-12">
                    <div>
                        <SectionHeader eyebrow="Die Technik" title={lensesHeadline} />
                        <div className="space-y-4 text-base md:text-lg text-hf-muted leading-relaxed">
                            {lensesBody ? (
                                <RichText html={lensesBody} testId="lenses-body" />
                            ) : (
                                lensesParas.map((p, i) => <p key={i}>{p}</p>)
                            )}
                        </div>
                    </div>
                    <div
                        className="w-full xl:w-auto"
                        role="img"
                        aria-label="Zwei 180°-Fisheye-Linsen ergeben eine vollständige 360°-Kugel"
                    >
                        <div className="flex flex-col md:flex-row items-center justify-center gap-4 lg:gap-6">
                            {/* FRONT 180° */}
                            <div className="flex flex-col items-center gap-3">
                                <img
                                    src="/technik/lens-front-180.png"
                                    alt="Frontlinse mit 180°-Sichtfeld — Fisheye-Optik"
                                    loading="lazy"
                                    className="block object-contain shrink-0 transition-transform duration-500 ease-out motion-safe:hover:scale-[1.04] w-[180px] h-[180px]"
                                    style={{ background: "transparent" }}
                                />
                                <span
                                    className="text-[11px] tracking-[0.22em] uppercase font-medium"
                                    style={{ color: "hsl(var(--hf-gold-dark))" }}
                                >
                                    Front
                                </span>
                            </div>

                            {/* + Konnektor */}
                            <span
                                aria-hidden
                                className="hidden md:block font-display text-2xl select-none md:-mt-6 mx-1 lg:mx-2 font-medium"
                                style={{ color: "#D4AF37" }}
                            >
                                +
                            </span>

                            {/* REAR 180° */}
                            <div className="flex flex-col items-center gap-3">
                                <img
                                    src="/technik/lens-rear-180.png"
                                    alt="Rücklinse mit 180°-Sichtfeld — Fisheye-Optik"
                                    loading="lazy"
                                    className="block object-contain shrink-0 transition-transform duration-500 ease-out motion-safe:hover:scale-[1.04] w-[180px] h-[180px]"
                                    style={{ background: "transparent" }}
                                />
                                <span
                                    className="text-[11px] tracking-[0.22em] uppercase font-medium"
                                    style={{ color: "hsl(var(--hf-gold-dark))" }}
                                >
                                    Rear
                                </span>
                            </div>

                            {/* = Konnektor */}
                            <span
                                aria-hidden
                                className="hidden md:block font-display text-2xl select-none md:-mt-6 mx-1 lg:mx-2 font-medium"
                                style={{ color: "#D4AF37" }}
                            >
                                =
                            </span>

                            {/* 360° KUGEL — Hero, Höhe responsive, Aspect erhalten */}
                            <div className="flex flex-col items-center gap-3 md:-translate-y-3">
                                <img
                                    src="/technik/sphere-360.png"
                                    alt="360°-Kugel — vollständige räumliche Aufnahme"
                                    loading="lazy"
                                    className="block object-contain shrink-0 transition-transform duration-500 ease-out motion-safe:hover:scale-[1.04] h-[300px] w-auto"
                                    style={{ background: "transparent" }}
                                />
                                <span
                                    className="text-[11px] tracking-[0.22em] uppercase font-medium"
                                    style={{ color: "hsl(var(--hf-gold-dark))" }}
                                >
                                    360° Kugel
                                </span>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
            )}

            {/* COMPARE */}
            {showCompare && (
            <section id="compare" data-testid="section-compare" className="py-20 md:py-32" style={{ background: "hsl(var(--hf-surface) / 0.6)" }}>
                <Container>
                    {(() => {
                        const homeCompareLead = getObject<{ lead?: string }>("home.compare", {});
                        const compareLead = typeof homeCompareLead.lead === "string" ? homeCompareLead.lead : "";
                        return (
                            <>
                                <SectionHeader eyebrow="Vergleich" title="Foto · Video · 360°" />
                                {compareLead ? (
                                    <div className="mt-4 max-w-3xl text-base md:text-lg text-hf-muted leading-relaxed" data-testid="compare-lead">
                                        <RichText html={compareLead} />
                                    </div>
                                ) : (
                                    <p className="mt-4 max-w-3xl text-base md:text-lg text-hf-muted leading-relaxed">
                                        Warum HypeFly 360° ein anderes Werkzeug ist als klassische Aufnahmen.
                                    </p>
                                )}
                            </>
                        );
                    })()}
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {(() => {
                            // CMS home.compare.cards überschreibt statische COMPARE-Konstante.
                            type Card = { key?: string; title: string; icon?: string; highlight?: boolean; bullets?: string[] };
                            const homeCompare = getObject<{ cards?: Card[] }>("home.compare", {});
                            const cards: Card[] = (homeCompare.cards && homeCompare.cards.length > 0) ? homeCompare.cards.filter(isEnabled) : COMPARE;
                            return cards.map((c, i) => {
                            const icon = c.icon === "camera" ? <Camera size={20} /> : c.icon === "film" ? <Film size={20} /> : <Sparkles size={20} />;
                            const key = c.key || `card-${i}`;
                            return (
                                <article
                                    key={key}
                                    data-testid={`compare-card-${key}`}
                                    className="p-7 rounded-sm relative"
                                    style={{
                                        background: c.highlight ? "hsl(var(--hf-bg))" : "hsl(var(--hf-bg))",
                                        border: c.highlight
                                            ? "1px solid hsl(var(--hf-gold))"
                                            : "1px solid hsl(var(--hf-line))",
                                        boxShadow: c.highlight ? "var(--shadow-warm-md)" : "none",
                                    }}
                                >
                                    <div
                                        className="inline-flex items-center justify-center w-10 h-10 rounded-sm"
                                        style={{
                                            background: c.highlight
                                                ? "hsl(var(--hf-gold-soft))"
                                                : "hsl(var(--hf-surface))",
                                            color: c.highlight ? "hsl(var(--hf-gold-dark))" : "hsl(var(--hf-ink))",
                                        }}
                                    >
                                        {icon}
                                    </div>
                                    <h3 className="font-display text-2xl mt-4">{c.title}</h3>
                                    <span
                                        className="inline-block w-8 h-px mt-3"
                                        style={{ background: "hsl(var(--hf-gold))" }}
                                    />
                                    <ul className="mt-4 space-y-2 text-sm text-hf-muted">
                                        {(c.bullets || []).map((b, j) => (
                                            <li key={j} className="flex gap-2">
                                                <span aria-hidden style={{ color: "hsl(var(--hf-gold-dark))" }}>·</span>
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            );
                            });
                        })()}
                    </div>
                </Container>
            </section>
            )}

            {/* USE CASES — Bild-Kacheln (deutscher Kontext, kein Icon-Layout mehr) */}
            {showUseCases && (
            <section id="use-cases" data-testid="section-use-cases" className="py-20 md:py-32">
                <Container>
                    <SectionHeader eyebrow="Einsatz" title="Wann HypeFly der richtige Zeitpunkt ist." lead="Eine 360°-Video-Dokumentation bleibt — und wird oft gerade dann zum Schatz, wenn sich etwas ändert." />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                        {(() => {
                            // Wenn CMS-Kacheln vorhanden → CMS ist Quelle der Wahrheit (Anzahl + Reihenfolge).
                            // Sonst fallback auf statische USE_CASES.
                            //
                            // WICHTIG (Löschen persistieren): Für `image` KEIN Fallback auf
                            // `staticMatch.image` mehr — sonst kehrt beim Entfernen eines Mediums
                            // die alte Stock-Grafik zurück und der User sieht die vermeintlich
                            // gelöschte Kachel unverändert weiter. `body`/`alt` bleiben tolerant,
                            // damit sie nach initialem Seed sinnvolle Defaults liefern.
                            type CmsTile = { title?: string; body?: string; image?: MediaRef; skybox?: MediaRef; alt?: string; enabled?: boolean };
                            const cmsTiles = ((homeUseCases.tiles || []) as CmsTile[]).filter(isEnabled);
                            type StaticTile = typeof USE_CASES[number];
                            const tiles: { key: string; title: string; body?: string; image?: MediaRef; skybox?: MediaRef; alt?: string }[] =
                                cmsTiles.length > 0
                                    ? cmsTiles.map((t, i) => {
                                        const staticMatch = USE_CASES.find((u) => u.title === t.title) as StaticTile | undefined;
                                        return {
                                            key: `cms-${i}`,
                                            title: t.title || staticMatch?.title || "",
                                            body: t.body || staticMatch?.body,
                                            image: t.image, // kein static-Fallback — Löschen bleibt Löschen
                                            skybox: t.skybox,
                                            alt: t.alt || staticMatch?.alt,
                                        };
                                    })
                                    : USE_CASES.map((u, i) => ({ key: `static-${i}`, title: u.title, body: u.body, image: u.image as unknown as MediaRef, alt: u.alt }));
                            return tiles.map((u) => (
                                <UseCaseTile
                                    key={u.key}
                                    title={u.title}
                                    body={u.body}
                                    image={u.image}
                                    skybox={u.skybox}
                                    alt={u.alt}
                                />
                            ));
                        })()}
                    </div>
                </Container>
            </section>
            )}
            {/* old (pre-2025-image-tiles): USE_CASES als Icon-Kacheln — zurücksetzen bei Bedarf:
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {USE_CASES.map((u) => (
                        <div key={u.title} className="p-6 rounded-sm ...">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-sm ...">
                                {USE_ICON_MAP[u.icon] || <Sparkles size={20} />}
                            </span>
                            <div className="font-display text-lg mt-4">{u.title}</div>
                            <p className="mt-1.5 text-sm text-hf-muted">{u.body}</p>
                        </div>
                    ))}
                </div>
            */}

            {/* GALLERY */}
            {showGallery && (
            <section id="gallery" data-testid="section-gallery" className="py-20 md:py-32" style={{ background: "hsl(var(--hf-surface) / 0.6)" }}>
                <Container>
                    <SectionHeader eyebrow="Beispiele" title="Ein Blick in unsere Mediengalerie." lead="Klick auf eine Kachel öffnet den passenden Viewer — für 360°-Video, 360°-Foto oder klassische Aufnahmen." />
                    <HomeGallery />
                </Container>
            </section>
            )}

            {/* VORHER · NACHHER */}
            {showBeforeAfter && <BeforeAfterSection source="main" />}
            {showTr360 && <Transformations360Section />}

            {/* DISKRETIONS-STATEMENT — standardmäßig aus, Admin-Toggle in AdminStartseite */}
            {get<boolean>("home.show_discretion", false) && <DiscretionSection />}

            {/* PROCESS */}
            {showProcess && (
            <section id="process" data-testid="section-process" className="py-20 md:py-32">
                <Container>
                    {(() => {
                        // CMS home.process.steps überschreibt PROCESS.
                        type Step = { step: string; title: string; body?: string };
                        const homeProcess = getObject<{ lead?: string; steps?: Step[]; optional_note?: string }>("home.process", {});
                        const steps: Step[] = (homeProcess.steps && homeProcess.steps.length > 0) ? homeProcess.steps.filter(isEnabled) : PROCESS;
                        const optionalNote = typeof homeProcess.optional_note === "string" ? homeProcess.optional_note : "Sichere dauerhafte Archivierung Ihrer Aufnahmen — auf Wunsch inklusive im Legacy-Paket.";
                        const processLead = typeof homeProcess.lead === "string" ? homeProcess.lead : "";
                        return (
                            <>
                                <SectionHeader eyebrow="Ablauf" title="Vier Schritte. Klar geplant." />
                                {processLead ? (
                                    <div className="mt-4 max-w-3xl text-base md:text-lg text-hf-muted leading-relaxed" data-testid="process-lead">
                                        <RichText html={processLead} />
                                    </div>
                                ) : (
                                    <p className="mt-4 max-w-3xl text-base md:text-lg text-hf-muted leading-relaxed">
                                        Vom ersten Gespräch bis zur digitalen Übergabe — transparent und sorgfältig.
                                    </p>
                                )}
                                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {steps.map((p) => (
                                        <div key={p.step} data-testid={`process-step-${p.step}`}>
                                            <div
                                                className="inline-flex items-center justify-center w-14 h-14 rounded-full font-display text-2xl"
                                                style={{
                                                    border: "1px solid hsl(var(--hf-gold))",
                                                    color: "hsl(var(--hf-gold-dark))",
                                                    background: "hsl(var(--hf-gold-soft) / 0.35)",
                                                }}
                                            >
                                                {p.step}
                                            </div>
                                            <div className="font-display text-xl mt-5">{p.title}</div>
                                            <p className="mt-2 text-sm text-hf-muted leading-relaxed">{p.body}</p>
                                        </div>
                                    ))}
                                </div>
                                {optionalNote.trim() && (
                                    <div
                                        className="mt-10 p-6 rounded-sm text-sm text-hf-muted"
                                        style={{
                                            background: "hsl(var(--hf-bg))",
                                            border: "1px dashed hsl(var(--hf-line))",
                                        }}
                                    >
                                        <span className="hf-eyebrow" style={{ marginRight: 10 }}>Optional · 05</span>
                                        {optionalNote}
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </Container>
            </section>
            )}

            {/* PACKAGES */}
            {showPackages && (
            <section id="packages" data-testid="section-packages" className="py-20 md:py-32" style={{ background: "hsl(var(--hf-dark))", color: "hsl(var(--hf-bg))" }}>
                <Container>
                    <div className="max-w-3xl">
                        <div className="hf-eyebrow" style={{ color: "hsl(var(--hf-gold-soft))" }}>Pakete</div>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.1]">
                            Drei Wege, Ihre Immobilie zu bewahren.
                        </h2>
                        <p className="mt-5 text-hf-bg/80 md:text-lg">Jedes Paket ist ein klarer Rahmen — den konkreten Umfang stimmen wir individuell ab.</p>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {(() => {
                            // CMS home.packages.items überschreibt PACKAGES.
                            type Pkg = { key?: string; name: string; recommended?: boolean; bullets?: string[]; price?: string };
                            const homePkg = getObject<{ items?: Pkg[] }>("home.packages", {});
                            const pkgs: Pkg[] = (homePkg.items && homePkg.items.length > 0) ? homePkg.items.filter(isEnabled) : PACKAGES;
                            return pkgs.map((p, i) => {
                            const key = p.key || `pkg-${i}`;
                            return (
                            <article
                                key={key}
                                data-testid={`package-${key}`}
                                className="p-7 rounded-sm relative flex flex-col"
                                style={{
                                    background: p.recommended
                                        ? "hsl(var(--hf-bg))"
                                        : "hsl(var(--hf-dark-2))",
                                    color: p.recommended ? "hsl(var(--hf-ink))" : "hsl(var(--hf-bg))",
                                    border: p.recommended
                                        ? "1px solid hsl(var(--hf-gold))"
                                        : "1px solid rgba(255,255,255,0.08)",
                                }}
                            >
                                {p.recommended && (
                                    <span
                                        className="absolute -top-3 left-6 text-[10px] tracking-[0.16em] uppercase px-3 py-1 rounded-sm"
                                        style={{
                                            background: "hsl(var(--hf-gold))",
                                            color: "hsl(var(--hf-ink))",
                                        }}
                                    >
                                        Empfohlen
                                    </span>
                                )}
                                <h3 className="font-display text-2xl md:text-3xl">{p.name}</h3>
                                <span
                                    className="inline-block w-8 h-px mt-3"
                                    style={{ background: "hsl(var(--hf-gold))" }}
                                />
                                <ul className="mt-5 space-y-2.5 text-sm flex-1">
                                    {(p.bullets || []).map((b, j) => (
                                        <li key={j} className="flex gap-2">
                                            <span aria-hidden style={{ color: "hsl(var(--hf-gold-dark))" }}>·</span>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div
                                    className="mt-6 text-sm"
                                    style={{
                                        color: p.recommended
                                            ? "hsl(var(--hf-gold-dark))"
                                            : "hsl(var(--hf-gold-soft))",
                                    }}
                                >
                                    {p.price}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => pickPackage(p.name)}
                                    className="hf-btn hf-btn-primary mt-5 justify-center"
                                    data-testid={`package-cta-${key}`}
                                >
                                    Unverbindlich anfragen <span aria-hidden>→</span>
                                </button>
                            </article>
                            );
                            });
                        })()}
                    </div>
                </Container>
            </section>
            )}

            {/* ABOUT */}
            {showAbout && (
            <section id="about" data-testid="section-about" className="py-20 md:py-32">
                <Container className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <SectionHeader eyebrow={ABOUT.eyebrow} title={aboutHeadline} />
                        <div className="space-y-5 text-base md:text-lg text-hf-muted leading-relaxed">
                            {aboutBody ? (
                                <RichText html={aboutBody} testId="about-body" />
                            ) : (
                                aboutParas.map((p, i) => <p key={i}>{p}</p>)
                            )}
                        </div>
                    </div>
                    <SmartMedia
                        mediaRef={homeAbout.image as MediaRef}
                        /* Static-Fallback nur wenn nie gesetzt (siehe „Die Idee"). */
                        fallbackUrl={homeAbout.image === undefined ? ABOUT.image : undefined}
                        alt="Warme editoriale Aufnahme aus einem Beratungsgespräch"
                        width={800}
                        height={600}
                        loading="lazy"
                        className="w-full h-full object-cover rounded-sm"
                        testId="about-image"
                        interactive360={true}
                        videoAutoplay
                        videoMuted
                        videoLoop
                        videoControls={false}
                    />
                </Container>
            </section>
            )}

            {/* FAQ */}
            {showFaq && (
            <section id="faq" data-testid="section-faq" className="py-20 md:py-32" style={{ background: "hsl(var(--hf-surface) / 0.6)" }}>
                <Container className="max-w-3xl">
                    <SectionHeader eyebrow="Fragen" title="Häufig gestellte Fragen" />
                    <div className="divide-y" style={{ borderColor: "hsl(var(--hf-line))" }}>
                        {(() => {
                            // FAQ-Items: CMS-Section überschreibt vollständig, sonst static
                            interface FAQCms { items?: { q?: string; a?: string }[] }
                            const homeFaq = getObject<FAQCms>("home.faq", {});
                            const cmsItems = (homeFaq.items || []).filter(isEnabled);
                            const faqList = cmsItems.length > 0 ? cmsItems : FAQ;
                            return faqList.map((f, i) => {
                            const open = openFaq === i;
                            return (
                                <div key={i} data-testid={`faq-item-${i}`}>
                                    <button
                                        type="button"
                                        onClick={() => setOpenFaq(open ? null : i)}
                                        className="w-full flex items-center justify-between gap-4 py-5 text-left"
                                        aria-expanded={open}
                                        data-testid={`faq-toggle-${i}`}
                                    >
                                        <span className="font-display text-lg md:text-xl text-hf-ink">{f.q}</span>
                                        <ChevronDown
                                            size={20}
                                            className="shrink-0 transition-transform"
                                            style={{
                                                color: "hsl(var(--hf-gold-dark))",
                                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                                            }}
                                        />
                                    </button>
                                    {open && (
                                        <div className="pb-5 text-hf-muted text-base leading-relaxed">
                                            {f.a}
                                        </div>
                                    )}
                                </div>
                            );
                            });
                        })()}
                    </div>
                </Container>
            </section>
            )}

            {/* CONTACT */}
            {showContact && (
            <section id="kontakt" data-testid="section-contact" className="py-20 md:py-32">
                <Container className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
                    {/* Formular zuerst (Mobile: oben · Desktop: linke Spalte, breiter).
                     *  Der Anchor `#kontakt-formular` erlaubt direkte Sprünge
                     *  aus CTA-Buttons, sodass der User beim Ankommen sofort
                     *  das Name-Feld sieht. */}
                    <div className="lg:col-span-3 order-1" id="kontakt-formular" data-testid="contact-form-wrapper">
                        <ContactForm initialPackage={contactPkg} />
                    </div>
                    <div className="lg:col-span-2 order-2">
                        <SectionHeader eyebrow="Kontakt" title="Sprechen wir über Ihr Projekt." />
                        <p className="text-hf-muted leading-relaxed">
                            Ein kurzes Erstgespräch reicht, um Umfang, Ablauf und die passende Variante zu klären. Vertraulich, unverbindlich, freundlich.
                        </p>
                        <div className="mt-8 space-y-4">
                            <a
                                href={`tel:${BDV_CONTACT.phoneHref}`}
                                className="flex items-start gap-3 group"
                                data-testid="contact-info-phone"
                            >
                                <Phone size={18} style={{ color: "hsl(var(--hf-gold-dark))" }} className="mt-1" />
                                <span>
                                    <span className="hf-eyebrow" style={{ fontSize: "10px" }}>Telefon</span>
                                    <span className="block text-hf-ink group-hover:text-hf-gold-dark transition-colors">
                                        {contactPhone}
                                    </span>
                                </span>
                            </a>
                            <a
                                href={`mailto:${contactEmail}`}
                                className="flex items-start gap-3 group"
                                data-testid="contact-info-email"
                            >
                                <Mail size={18} style={{ color: "hsl(var(--hf-gold-dark))" }} className="mt-1" />
                                <span>
                                    <span className="hf-eyebrow" style={{ fontSize: "10px" }}>E-Mail</span>
                                    <span className="block text-hf-ink group-hover:text-hf-gold-dark transition-colors">
                                        {contactEmail}
                                    </span>
                                </span>
                            </a>
                            <div className="flex items-start gap-3" data-testid="contact-info-address">
                                <MapPin size={18} style={{ color: "hsl(var(--hf-gold-dark))" }} className="mt-1" />
                                <span>
                                    <span className="hf-eyebrow" style={{ fontSize: "10px" }}>Anschrift</span>
                                    <span className="block text-hf-ink whitespace-pre-line">
                                        {BDV_CONTACT.company}
                                        {"\n"}
                                        {contactAddress}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
            )}

            </main>
            <HFFooter />
        </div>
    );
}
